import moment from 'moment';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React, { Suspense } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './configs/setupAdditivServices';
import './configs/setupIdentityServerConfig';
import './configs/setupNotificationHubConfig';
import './configs/setupStorageTokensConfig';
import './configs/setupAutoLogoutConfig';
import './configs/setupHighcharts';
import { Provider } from 'react-redux';
import { NotificationProvider } from 'libs/additiv-react-signalr';
import AuthenticationProvider from 'domain/Auth/AuthProvider';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ActiveSystemMessageProvider } from 'prodivers/ActiveSystemMessageProvider';
import * as serviceWorker from './serviceWorker';
import store from './store';
import App from './App';
import { LocaleProvider } from './locale';
import { LayoutProvider } from './prodivers/layout';

import ErrorHandler from './errors';
import SM from './services/ServiceManager';
import 'styles';
import './translations/i18n';

import 'clientRequirements/styles/cutomStyles.css';
import 'formiojs/dist/formio.full.min.css';
import 'ui-library/components/TabbedNavigation/TabbedNavigation.css';

moment();

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

const errorHandlerLog = (errorModel: { type: string; stack: string }) => {
    const styles = {
        title: 'color: brown; font-size: 16px; font-weight: bold;',
        type: 'color: white; background-color: brown; font-size: 14px; font-weight: bold; border-radius: 3px;',
        stack: 'color: brown',
    };

    console.error(`%c⚠️ New Error with type: %c ${errorModel.type} \n%c${errorModel.stack}`, styles.title, styles.type, styles.stack);
};

ErrorHandler.configure({
    log: errorHandlerLog,
    DFSLoggerConfig: {
        enabled: true,
        endpointFunc: (data: unknown) => SM.middleware('log', [data]),
    },
});

const rootElement = document.getElementById('root');

if (rootElement) {
    const root = createRoot(rootElement);

    root.render(
        <QueryClientProvider client={queryClient}>
            <Provider store={store}>
                <LocaleProvider>
                    <LayoutProvider>
                        <BrowserRouter>
                            <AuthenticationProvider>
                                <NotificationProvider>
                                    <ActiveSystemMessageProvider clientApplication={1}>
                                        <Suspense fallback={<div>Loading translations...</div>}>
                                            <React.StrictMode>
                                                <App />
                                            </React.StrictMode>
                                        </Suspense>
                                    </ActiveSystemMessageProvider>
                                </NotificationProvider>
                            </AuthenticationProvider>
                        </BrowserRouter>
                    </LayoutProvider>
                </LocaleProvider>
            </Provider>

            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>,
    );
}

serviceWorker.unregister();
